import { forwardRef } from "react";

import { Popover } from "@magnetic/popover";
import { Text } from "@magnetic/text";

import { withAdminGuard } from "../hoc/withAdminGuard";
import { withEditModeGuard } from "../hoc/withEditModeGuard";
import { withReadOnlyGuard } from "../hoc/withReadOnlyGuard";
import { withSoftwareUpgradeGuard } from "../hoc/withSoftwareUpgradeGuard";

import { Button } from "./button";
import { ButtonScope, EditButtonProps } from "./types";

/**
 * Inner button used by both the write-access and fabric-edit-mode variants.
 * Not exported — consumers should always use `EditButton`.
 */
const EditButtonInner = forwardRef<HTMLButtonElement, EditButtonProps>(
  (props: EditButtonProps, ref) => {
    const { children, tooltip, ...rest } = props;

    if (tooltip) {
      return (
        <Popover
          kind="map"
          className="popover-tooltip"
          title={<Text size="p3">{tooltip}</Text>}
        >
          <Button name="editButton" ref={ref} {...rest}>
            {children}
          </Button>
        </Popover>
      );
    }

    return (
      <Button ref={ref} {...rest}>
        {children}
      </Button>
    );
  }
);

/**
 * Write-access variant: hidden for read-only users.
 * No fabric-scoped guards (edit mode / software upgrade).
 */
const WriteAccessButton = withReadOnlyGuard(EditButtonInner);

/**
 * Fabric-scoped variant: hidden for read-only users,
 * hidden when the fabric is not in candidate-edit mode,
 * and disabled during software-upgrade maintenance mode.
 *
 * All three guards rely on context providers that are
 * only available inside a fabric route (DataProvider).
 */
const FabricEditModeButton = withSoftwareUpgradeGuard(
  withReadOnlyGuard(withEditModeGuard(EditButtonInner))
);

/**
 * Admin-only variant: hidden for non-admin users.
 * Only users with the ADMIN role can see this button.
 */
const AdminOnlyButton = withAdminGuard(EditButtonInner);

/**
 * A button that is always hidden from read-only users.
 *
 * By default (`scope=fabricEditModeOnly`), two additional fabric-scoped guards apply:
 * - Hidden when the fabric is not in candidate-edit mode.
 * - Disabled (with tooltip) when the fabric is in software-upgrade
 *   maintenance mode (unless `ignoreSoftwareMode` is set).
 *
 * Use `scope=writeAccess` on app-wide pages (notifications, API tokens, etc.)
 * where only the read-only guard should apply.
 */

export const EditButton = forwardRef<HTMLButtonElement, EditButtonProps>(
  (props: EditButtonProps, ref) => {
    const { scope, ...rest } = props;
    if (scope === ButtonScope.writeAccess) {
      return <WriteAccessButton {...rest} ref={ref} />;
    }
    if (scope === ButtonScope.adminOnly) {
      return <AdminOnlyButton {...rest} ref={ref} />;
    }
    return <FabricEditModeButton {...rest} ref={ref} />;
  }
);
